import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { apiBeta } from 'src/client/api';
import { player } from 'src/client/player';
import { stores } from 'src/client/stores';
import * as PlayerActions from 'src/common/actions/player';
import { PLAYER_MANAGER_ENABLED } from 'src/common/constants/experiments/config';
import { playerStatuses } from 'src/common/constants/playerStatuses';
import { selectExperiment } from 'src/common/selectors/config';
import { selectIsMobile } from '../selectors/app';
import {
  selectAutoPlayGuideItem,
  selectCanScrub,
  selectExternalHtmlPlayerUrl,
  selectIsMediaAdLoaded,
  selectNowPlaying,
  selectPlayerStatus,
  selectTunedGuideId,
} from '../selectors/player';

export const syncedPlayerActions = {
  [playerStatuses.playing]: (actions, canScrub) => {
    if (canScrub) {
      actions.pause();
      apiBeta.reportPlayerPauseClick();
      return;
    }

    actions.stop();
    apiBeta.reportPlayerStopClick();
  },
  [playerStatuses.paused]: (actions) => actions.play(),
  [playerStatuses.stopped]: (actions, canScrub, guideItem, disablePreroll) =>
    actions.retune({ disablePreroll }),
  [playerStatuses.failed]: (actions, canScrub, guideItem, disablePreroll) =>
    actions.retune({ disablePreroll }),
  [playerStatuses.idle]: (actions, canScrub, guideItem, disablePreroll) => {
    if (guideItem) {
      return actions.tune({ guideItem, disablePreroll });
    }

    return noop;
  },
};
const playerManagerPlayerAction = (_, __, guideItem, skipPreroll) =>
  player.onClick(guideItem, { skipPreroll });

export function mapStateToProps(state) {
  const isPlayerManagerEnabled = selectExperiment(
    state,
    PLAYER_MANAGER_ENABLED,
  );
  const nowPlaying = selectNowPlaying(state);

  return {
    playerStatus: selectPlayerStatus(state),
    canScrub: isPlayerManagerEnabled
      ? !nowPlaying?.isLive || nowPlaying?.isLiveSeek
      : selectCanScrub(state),
    tunedGuideId: selectTunedGuideId(state),
    externalHtmlPlayerUrl: selectExternalHtmlPlayerUrl(state),
    isMobile: selectIsMobile(state),
    legacyAutoPlayGuideItem: selectAutoPlayGuideItem(state),
    isMediaAdLoaded: selectIsMediaAdLoaded(state),
    isPlayerManagerEnabled,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    playerActions: bindActionCreators(PlayerActions, dispatch),
  };
}

export default function connectWithPlayer(MyComponent) {
  /* eslint-disable react/prefer-stateless-function */
  class ConnectWithPlayer extends Component {
    static propTypes = {
      guideItem: PropTypes.object,
      playerStatus: PropTypes.string.isRequired,
      actions: PropTypes.object,
      isMobile: PropTypes.bool.isRequired,
      legacyAutoPlayGuideItem: PropTypes.object,
      canScrub: PropTypes.bool,
      tunedGuideId: PropTypes.string,
      isMediaAdLoaded: PropTypes.bool.isRequired,
      isPlayerManagerEnabled: PropTypes.bool,
    };

    state = {
      autoplayGuideItem: null,
    };

    componentDidMount() {
      this.getAutoPlayGuideItem();
    }

    getAutoPlayGuideItem() {
      player.ready.then(() => {
        this.setState({
          autoplayGuideItem:
            stores.playerStore.getState().metadata?.autoplayGuideItem,
        });
      });
    }

    render() {
      const {
        playerStatus,
        guideItem,
        tunedGuideId,
        legacyAutoPlayGuideItem,
        isPlayerManagerEnabled,
      } = this.props;
      const { autoplayGuideItem } = this.state;
      const playGuideId = guideItem?.actions?.play?.guideId;
      const isPrimedForAutoPlay =
        playerStatus === playerStatuses.idle &&
        playGuideId ===
          (autoplayGuideItem?.playableGuideId ||
            legacyAutoPlayGuideItem?.guideId);
      const isSyncedWithPlayer = !!(
        playGuideId &&
        playGuideId === tunedGuideId &&
        // we still need to tune in the case when !isPrimedForAutoPlay which is why
        // it's not considered to be in sync with player
        !isPrimedForAutoPlay
      );
      const syncedPlayerStatus = isSyncedWithPlayer
        ? playerStatus
        : playerStatuses.idle;
      const syncedPlayerAction =
        syncedPlayerActions[syncedPlayerStatus] || noop;

      return (
        <MyComponent
          {...this.props}
          isSyncedWithPlayer={isSyncedWithPlayer}
          syncedPlayerStatus={syncedPlayerStatus}
          syncedPlayerAction={
            isPlayerManagerEnabled
              ? playerManagerPlayerAction
              : syncedPlayerAction
          }
        />
      );
    }
  }

  return connect(mapStateToProps, mapDispatchToProps)(ConnectWithPlayer);
}
